import React, { useState } from 'react';
import { FaBell, FaInfoCircle, FaPaperPlane, FaCheck, FaMobileAlt } from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import { showToast } from '../utils/toast';
import api from '../../../services/api';

const TITLE_MAX = 35;
const SUBTITLE_MAX = 75;

const NotifyPage = () => {
  const { brand, brandName, brandLogo } = useProContext();
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const handleSubmit = async () => {
    if (!title.trim() || !subtitle.trim()) {
      showToast.error('Titre et sous-titre requis');
      return;
    }
    if (title.length > TITLE_MAX || subtitle.length > SUBTITLE_MAX) {
      showToast.error('Texte trop long');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await api.post('/opticians/send-notification', { title, subtitle }, { headers: { Authorization: `Bearer ${token}` } });
      setSent(data);
      setTitle('');
      setSubtitle('');
    } catch (error) {
      showToast.error(error.response?.data?.message || 'Erreur lors de l\'envoi');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <ProLayout title="Notifier les utilisateurs">
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
            <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#10B98115' }}>
              <FaCheck className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Notification envoyée !</h2>
            <p className="text-sm text-gray-500 mb-6">
              Envoyée à <strong className="text-gray-900">{sent.successCount || 0}</strong> utilisateur{(sent.successCount || 0) > 1 ? 's' : ''}
              {sent.errorCount > 0 && ` (${sent.errorCount} échec${sent.errorCount > 1 ? 's' : ''})`}
            </p>
            <button
              onClick={() => setSent(null)}
              className="px-5 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90"
              style={{ backgroundColor: brand }}
            >
              Envoyer une autre notification
            </button>
          </div>
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Notifier les utilisateurs">
      <Toaster />
      <div className="p-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* ═══ LEFT: Form (3 cols) ═══ */}
          <div className="lg:col-span-3 space-y-4">
            {/* Info banner */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <FaInfoCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[0.8125rem] font-semibold text-amber-900">À quoi ça sert ?</p>
                <p className="text-[0.6875rem] text-amber-700 mt-0.5 leading-relaxed">
                  Envoyez une notification push à tous vos utilisateurs actifs. Limité à <strong>une notification par mois</strong> pour éviter la saturation.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[0.6875rem] font-semibold text-gray-500">Titre</p>
                  <p className={`text-[0.625rem] ${title.length > TITLE_MAX ? 'text-red-500' : 'text-gray-300'}`}>{title.length}/{TITLE_MAX}</p>
                </div>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Nouvelle collection printemps !"
                  maxLength={TITLE_MAX + 10}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[0.6875rem] font-semibold text-gray-500">Message</p>
                  <p className={`text-[0.625rem] ${subtitle.length > SUBTITLE_MAX ? 'text-red-500' : 'text-gray-300'}`}>{subtitle.length}/{SUBTITLE_MAX}</p>
                </div>
                <textarea
                  value={subtitle}
                  onChange={e => setSubtitle(e.target.value)}
                  placeholder="Venez découvrir les nouvelles montures en boutique, -20% sur toute la collection."
                  rows={3}
                  maxLength={SUBTITLE_MAX + 20}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
              <button
                onClick={handleSubmit}
                disabled={!title.trim() || !subtitle.trim() || sending || title.length > TITLE_MAX || subtitle.length > SUBTITLE_MAX}
                className="w-full flex items-center justify-center gap-2 py-3 text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:opacity-90"
                style={{ backgroundColor: brand }}
              >
                <FaPaperPlane className="w-3 h-3" />
                {sending ? 'Envoi en cours...' : 'Envoyer à tous les utilisateurs'}
              </button>
            </div>
          </div>

          {/* ═══ RIGHT: iPhone mockup (2 cols) ═══ */}
          <div className="lg:col-span-2">
            <p className="text-[0.6875rem] font-semibold text-gray-400 uppercase tracking-wider mb-3">Aperçu sur mobile</p>

            <div className="mx-auto" style={{ width: 280 }}>
              <div
                className="relative mx-auto"
                style={{
                  width: 280,
                  height: 570,
                  borderRadius: 48,
                  background: 'linear-gradient(145deg, #2a2a2c 0%, #1c1c1e 50%, #0a0a0a 100%)',
                  padding: 4,
                  boxShadow: '0 30px 60px -15px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05), inset 0 0 0 1px rgba(255,255,255,0.08)',
                }}
              >
                <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: 44, background: '#000', padding: 2 }}>
                  <div className="relative w-full h-full overflow-hidden" style={{ borderRadius: 42, backgroundImage: `linear-gradient(180deg, #1a1a2e 0%, #0f0f1e 50%, #000000 100%)` }}>
                    <div className="absolute inset-0 opacity-30" style={{ background: `radial-gradient(ellipse at top, ${brand}22 0%, transparent 50%), radial-gradient(ellipse at bottom, ${brand}15 0%, transparent 60%)` }} />

                    {/* Dynamic Island */}
                    <div className="absolute left-1/2 -translate-x-1/2 z-20" style={{ top: 12, width: 110, height: 32, background: '#000', borderRadius: 20 }} />

                    {/* Status bar */}
                    <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-7 pt-4">
                      <span className="text-[0.8125rem] text-white font-semibold" style={{ letterSpacing: -0.3 }}>9:41</span>
                      <div className="flex items-center gap-1">
                        <svg width="16" height="11" viewBox="0 0 16 11" fill="white">
                          <rect x="0" y="7" width="3" height="4" rx="0.5" />
                          <rect x="4" y="5" width="3" height="6" rx="0.5" />
                          <rect x="8" y="2" width="3" height="9" rx="0.5" />
                          <rect x="12" y="0" width="3" height="11" rx="0.5" />
                        </svg>
                        <svg width="14" height="11" viewBox="0 0 14 11" fill="white">
                          <path d="M7 11L9.1 8.7C8.5 8.2 7.8 8 7 8C6.2 8 5.5 8.2 4.9 8.7L7 11ZM11.5 5.9L13 4.3C11.4 2.7 9.3 1.8 7 1.8C4.7 1.8 2.6 2.7 1 4.3L2.5 5.9C3.7 4.7 5.3 4 7 4C8.7 4 10.3 4.7 11.5 5.9Z" />
                        </svg>
                        <div className="flex items-center">
                          <div className="relative border border-white/80 rounded" style={{ width: 22, height: 10, padding: 1 }}>
                            <div className="h-full bg-white rounded-sm" style={{ width: '80%' }} />
                          </div>
                          <div className="bg-white/80" style={{ width: 1, height: 4 }} />
                        </div>
                      </div>
                    </div>

                    {/* Lock screen */}
                    <div className="absolute inset-0 flex flex-col items-center pt-20 px-5">
                      <p className="text-[0.8125rem] text-white/80 font-medium">mercredi 10 avril</p>
                      <p className="text-white font-thin mt-0.5" style={{ fontSize: 84, lineHeight: 0.95, letterSpacing: -3 }}>9:41</p>

                      <div className="w-full mt-8">
                        {title || subtitle ? (
                          <div
                            className="w-full backdrop-blur-xl rounded-2xl p-3 transition-all duration-300"
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.85)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {brandLogo ? (
                                <img src={brandLogo} alt="" className="w-6 h-6 rounded-md object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: brand }}>
                                  <FaBell className="w-3 h-3 text-white" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[0.625rem] font-semibold text-gray-600 uppercase tracking-wide truncate">
                                  {brandName || 'Votre boutique'}
                                </p>
                              </div>
                              <p className="text-[0.625rem] text-gray-500">maintenant</p>
                            </div>
                            {title && <p className="text-[0.75rem] font-semibold text-gray-900 leading-tight mb-0.5">{title}</p>}
                            {subtitle && <p className="text-[0.6875rem] text-gray-700 leading-snug">{subtitle}</p>}
                          </div>
                        ) : (
                          <div className="w-full rounded-2xl p-6 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                            <FaMobileAlt className="w-5 h-5 text-white/40 mx-auto mb-2" />
                            <p className="text-[0.6875rem] text-white/50">Écrivez votre message pour voir l'aperçu</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Home indicator */}
                    <div className="absolute left-1/2 -translate-x-1/2 bg-white rounded-full" style={{ bottom: 8, width: 120, height: 4 }} />
                  </div>
                </div>

                {/* Side buttons */}
                <div className="absolute" style={{ left: -2, top: 100, width: 3, height: 30, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }} />
                <div className="absolute" style={{ left: -2, top: 150, width: 3, height: 50, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }} />
                <div className="absolute" style={{ left: -2, top: 210, width: 3, height: 50, background: '#1a1a1a', borderRadius: '2px 0 0 2px' }} />
                <div className="absolute" style={{ right: -2, top: 160, width: 3, height: 70, background: '#1a1a1a', borderRadius: '0 2px 2px 0' }} />
              </div>
            </div>

            <p className="text-[0.6875rem] text-gray-400 text-center mt-5 leading-relaxed">
              La notification sera envoyée<br />à tous vos utilisateurs actifs
            </p>
          </div>
        </div>
      </div>
    </ProLayout>
  );
};

export default NotifyPage;
